import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { batchedIn } from '@/lib/batchedQuery';
import { SCENARIO_CATEGORIES, scenarioMatchesCategory } from '@/config/scenarioCategories';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import { Search, Plus, DollarSign, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import type { ScenarioTemplate } from '@/lib/scenarioTemplates';

interface TemplateChooserProps {
  onSelectTemplate: (template: ScenarioTemplate) => void;
  onStartFromScratch: () => void;
}

// Map DB icon_name to emoji (fallback to clipboard)
const ICON_MAP: Record<string, string> = {
  Baby: '\uD83D\uDC76', Heart: '\u2764\uFE0F', Activity: '\uD83D\uDCCA',
  Ambulance: '\uD83D\uDE91', Pill: '\uD83D\uDC8A', Bone: '\uD83E\uDDB4',
  Stethoscope: '\uD83E\uDE7A', Brain: '\uD83E\uDDE0', Eye: '\uD83D\uDC41\uFE0F',
  Syringe: '\uD83D\uDC89', Scissors: '\u2702\uFE0F', Microscope: '\uD83D\uDD2C',
  Hospital: '\uD83C\uDFE5', Zap: '\u26A1', Shield: '\uD83D\uDEE1\uFE0F',
  Radiation: '\u2622\uFE0F', Dna: '\uD83E\uDDEC', FlaskConical: '\uD83E\uDDEA',
};

// Map DB category string to the category key used by SCENARIO_CATEGORIES
function mapDbCategoryToKey(dbCategory: string | null): string {
  if (!dbCategory) return 'surgery';
  const lower = dbCategory.toLowerCase();
  if (lower.includes('women') || lower.includes('ob')) return 'womens-health';
  if (lower.includes('heart') || lower.includes('cardio')) return 'cardiology';
  if (lower.includes('emergency') || lower.includes('critical')) return 'emergency';
  if (lower.includes('orthopedic') || lower.includes('joint')) return 'orthopedic';
  if (lower.includes('spine') || lower.includes('back') || lower.includes('pain')) return 'spine';
  if (lower.includes('cancer') || lower.includes('oncology')) return 'cancer';
  if (lower.includes('transplant')) return 'transplant';
  if (lower.includes('immune') || lower.includes('infectious')) return 'immune';
  if (lower.includes('diabetes') || lower.includes('endocrine')) return 'diabetes';
  if (lower.includes('pulmon') || lower.includes('respiratory') || lower.includes('lung')) return 'pulmonology';
  if (lower.includes('gene') || lower.includes('cell therap')) return 'gene-therapy';
  if (lower.includes('digest') || lower.includes('gi')) return 'surgery';
  return 'surgery';
}

export function TemplateChooser({ onSelectTemplate, onStartFromScratch }: TemplateChooserProps) {
  const { t } = useTranslation(['scenarios', 'common']);
  const brand = useBrand();
  const isAhp = brand.design === 'civic';
  const { isDark } = useTheme();
  const ahpDark = isAhp && isDark;
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Fetch scenarios + procedures from DB (same source as Scenarios page)
  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['scenario-templates'],
    queryFn: async () => {
      // Fetch active scenarios
      const { data: scenarios, error: scenErr } = await supabase
        .from('scenarios')
        .select('id, name, description, icon_name, duration, severity_level, tags, category, phases, estimated_cost')
        .eq('is_active', true)
        .eq('hidden_from_catalog', false)
        .order('name');

      if (scenErr) throw scenErr;
      if (!scenarios?.length) return [];

      // Fetch all procedures for these scenarios (batched to avoid URL length limits)
      const scenarioIds = scenarios.map(s => s.id);
      const { data: procedures, error: procErr } = await batchedIn(
        'scenario_procedures',
        'scenario_id',
        scenarioIds,
        'scenario_id, code, code_type, description, phase, quantity, estimated_cost, setting',
        { column: 'sort_order', ascending: true }
      );

      if (procErr) throw procErr;

      // Group procedures by scenario_id
      const procsByScenario = new Map<string, typeof procedures>();
      procedures?.forEach(p => {
        const list = procsByScenario.get(p.scenario_id) || [];
        list.push(p);
        procsByScenario.set(p.scenario_id, list);
      });

      // Now fetch real pricing for all unique codes (same approach as Scenarios page)
      const codesByType = new Map<string, Set<string>>();
      procedures?.forEach(p => {
        const ct = (p.code_type || 'CPT').toUpperCase();
        if (!codesByType.has(ct)) codesByType.set(ct, new Set());
        codesByType.get(ct)!.add(p.code);
      });

      const priceMap = new Map<string, number>();

      for (const [codeType, codes] of codesByType.entries()) {
        let tableName = 'cpt_pricing_averages';
        let codeColumn = 'cpt_code';
        let avgColumn = 'avg_gross_charge';

        if (codeType === 'CPT') {
          tableName = 'cpt_pricing_averages';
          codeColumn = 'cpt_code';
        } else if (codeType === 'DRG') {
          tableName = 'drg_pricing_averages';
          codeColumn = 'drg_code';
        } else if (codeType === 'DRUG' || codeType === 'DRUGS') {
          tableName = 'drug_pricing_averages';
          codeColumn = 'drug_code';
          avgColumn = 'avg_charge';
        } else if (codeType === 'SUPPLY') {
          tableName = 'supply_pricing_averages';
          codeColumn = 'supply_code';
          avgColumn = 'avg_charge';
        } else if (codeType === 'REV' || codeType === 'REVENUE') {
          tableName = 'revenue_pricing_averages';
          codeColumn = 'revenue_code';
          avgColumn = 'avg_charge';
        } else if (codeType === 'HCPCS') {
          tableName = 'hcpcs_pricing_averages';
          codeColumn = 'hcpcs_code';
        } else {
          continue;
        }

        const { data: priceData } = await supabase
          .from(tableName)
          .select(`${codeColumn}, setting_type, ${avgColumn}, total_hospitals`)
          .in(codeColumn, Array.from(codes));

        // Pick highest-hospital-count row per code+setting
        const bestPrices = new Map<string, any>();
        priceData?.forEach(row => {
          const code = row[codeColumn];
          const setting = row.setting_type || 'unspecified';
          const key = `${code}:${setting}`;
          const current = bestPrices.get(key);
          if (!current || (row.total_hospitals > current.total_hospitals)) {
            bestPrices.set(key, row);
          }
        });

        bestPrices.forEach(row => {
          const code = row[codeColumn];
          const setting = row.setting_type || 'unspecified';
          const price = Math.round(row[avgColumn] || 0);
          if (price > 0) {
            priceMap.set(`${codeType}:${code}:${setting}`, price);
          }
        });
      }

      // Build template objects
      return scenarios.map(s => {
        const procs = procsByScenario.get(s.id) || [];
        const tags = Array.isArray(s.tags) ? s.tags as string[] : [];
        const categoryKey = mapDbCategoryToKey(s.category);
        const phases = Array.isArray(s.phases) ? s.phases as string[] : [];

        const mappedProcs = procs.map(p => {
          const ct = (p.code_type || 'CPT').toUpperCase();
          const setting = p.setting || 'unspecified';

          // Look up real price: try exact setting -> all_settings -> unspecified
          let unitPrice = Number(p.estimated_cost) || 0;
          const keyExact = `${ct}:${p.code}:${setting}`;
          const keyAll = `${ct}:${p.code}:all_settings`;
          const keyUnspec = `${ct}:${p.code}:unspecified`;
          const realPrice = priceMap.get(keyExact) || priceMap.get(keyAll) || priceMap.get(keyUnspec);
          if (realPrice && realPrice > 0) {
            unitPrice = realPrice;
          }

          return {
            code: p.code,
            codeType: p.code_type || 'CPT',
            description: p.description || '',
            phase: p.phase || '',
            quantity: p.quantity || 1,
            estimatedCost: unitPrice,
            setting: (p.setting || 'unspecified') as 'inpatient' | 'outpatient' | 'unspecified',
          };
        });

        const totalCost = mappedProcs.reduce((sum, p) => sum + p.estimatedCost * p.quantity, 0);

        return {
          id: s.id,
          category: categoryKey,
          icon: ICON_MAP[s.icon_name || ''] || '\uD83D\uDCCB',
          shortDescription: s.description || '',
          severity: s.severity_level || 3,
          estimatedCost: totalCost,
          name: s.name,
          description: s.description || '',
          duration: s.duration || '',
          tags,
          phases,
          procedures: mappedProcs,
        } as ScenarioTemplate;
      });
    },
    staleTime: 5 * 60 * 1000, // Cache 5 minutes
  });

  const filteredTemplates = templates.filter(t => {
    const matchesSearch = !search ||
      t.name.toLowerCase().includes(search.toLowerCase()) ||
      t.shortDescription.toLowerCase().includes(search.toLowerCase()) ||
      t.tags.some(tag => tag.toLowerCase().includes(search.toLowerCase()));

    const matchesCategory = selectedCategory === 'all' ||
      t.category === selectedCategory ||
      // Also match via tags for broader coverage
      SCENARIO_CATEGORIES.some(cat =>
        cat.key === selectedCategory && scenarioMatchesCategory(t.tags, cat)
      );

    return matchesSearch && matchesCategory;
  });

  // Only show categories that have templates
  const categoriesWithTemplates = SCENARIO_CATEGORIES.filter(cat =>
    cat.key === 'all' || templates.some(t =>
      t.category === cat.key ||
      scenarioMatchesCategory(t.tags, cat)
    )
  );

  const severityLabel = (s: number) =>
    s <= 2 ? t('templateChooser.severity.routine') : s <= 5 ? t('templateChooser.severity.moderate') : s <= 7 ? t('templateChooser.severity.complex') : t('templateChooser.severity.critical');

  const severityColor = (s: number) =>
    s <= 2 ? 'text-green-600' : s <= 5 ? 'text-yellow-600' : s <= 7 ? 'text-orange-600' : 'text-red-600';

  return (
    <div className="space-y-4 py-2">
      <div className="text-center">
        <h2 className={`text-xl font-bold ${isAhp ? 'text-gray-900' : 'font-mono text-matrix-green'}`}>
          {t('templateChooser.title')}
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          {t('templateChooser.subtitle')}
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('templateChooser.searchPlaceholder')}
          className="pl-10 text-sm"
        />
      </div>

      {/* Category filter pills */}
      <div className="flex flex-wrap gap-1.5">
        {categoriesWithTemplates.map(cat => (
          <button
            key={cat.key}
            onClick={() => setSelectedCategory(cat.key)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
              selectedCategory === cat.key
                ? isAhp
                  ? 'bg-civic-money text-white'
                  : 'bg-matrix-green text-black'
                : isAhp
                  ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  : 'bg-white/5 text-muted-foreground hover:bg-white/10'
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className={`w-6 h-6 animate-spin ${isAhp ? 'text-civic-money' : 'text-matrix-green'}`} />
          <span className="ml-2 text-sm text-muted-foreground">{t('templateChooser.loading')}</span>
        </div>
      )}

      {/* Template grid */}
      {!isLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto thin-scrollbar pr-1">
          {filteredTemplates.map(tmpl => (
            <button
              key={tmpl.id}
              onClick={() => onSelectTemplate(tmpl)}
              className={`group text-left p-4 rounded-xl border-2 transition-all hover:scale-[1.01] ${
                ahpDark
                  ? 'border-civic-gold/20 hover:border-civic-gold/50 bg-civic-card-dark hover:bg-[var(--civic-navy-deep)]'
                  : isAhp
                  ? 'border-gray-200 hover:border-civic-money bg-white hover:bg-green-50/50'
                  : 'border-white/10 hover:border-matrix-green bg-black/20 hover:bg-matrix-green/5'
              }`}
            >
              <div className="flex items-start gap-3">
                <span className="text-2xl shrink-0">{tmpl.icon}</span>
                <div className="flex-1 min-w-0">
                  <h3 className={`text-sm font-bold leading-tight ${ahpDark ? 'text-civic-warm' : isAhp ? 'text-gray-900' : 'text-foreground'}`}>
                    {tmpl.name}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                    {tmpl.shortDescription}
                  </p>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="flex items-center gap-1 text-xs font-mono">
                      <DollarSign className="w-3 h-3 text-muted-foreground" />
                      <span className={ahpDark ? 'text-civic-warm' : isAhp ? 'text-gray-900' : 'text-foreground'}>
                        {tmpl.estimatedCost >= 1000000
                          ? `$${(tmpl.estimatedCost / 1000000).toFixed(1)}M`
                          : tmpl.estimatedCost >= 1000
                            ? `$${(tmpl.estimatedCost / 1000).toFixed(0)}k`
                            : `$${tmpl.estimatedCost.toLocaleString()}`
                        }
                      </span>
                    </span>
                    <span className={`text-[10px] font-medium ${severityColor(tmpl.severity)}`}>
                      {severityLabel(tmpl.severity)}
                    </span>
                    <Badge variant="outline" className="text-[9px] px-1.5 py-0">
                      {t('templateChooser.codesCount', { count: tmpl.procedures.length })}
                    </Badge>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {!isLoading && filteredTemplates.length === 0 && (
        <div className="text-center py-6">
          <p className="text-sm text-muted-foreground">{t('templateChooser.noResults')}</p>
        </div>
      )}

      {/* Start from scratch */}
      <button
        onClick={onStartFromScratch}
        className={`w-full p-3 rounded-lg border-2 border-dashed text-center transition-all hover:scale-[1.01] ${
          isAhp
            ? 'border-gray-300 hover:border-civic-money text-gray-500 hover:text-civic-money'
            : 'border-white/20 hover:border-matrix-green text-muted-foreground hover:text-matrix-green'
        }`}
      >
        <div className="flex items-center justify-center gap-2">
          <Plus className="w-4 h-4" />
          <span className="text-sm font-medium">{t('templateChooser.startFromScratch')}</span>
        </div>
        <p className="text-[10px] text-muted-foreground mt-1">{t('templateChooser.startFromScratchDescription')}</p>
      </button>
    </div>
  );
}
